import * as fs from "node:fs";
import * as net from "node:net";
import * as os from "node:os";
import * as path from "node:path";
import { spawn } from "node:child_process";
import type { TokenCredential } from "@azure/identity";
import { resolveFoundryProject } from "./foundry-project.js";
import { runAz } from "./az.js";
import { LocalCliError } from "./errors.js";
import { grantFoundryUser } from "./rbac.js";
import { tryOpenUrl } from "./gh-helpers.js";

export interface FoundryEndpointInfo { endpoint: string; accountScope: string }

/**
 * Poll for the Foundry project the cloud installer creates in the founder's
 * subscription. resolveFoundryProject throws while no AIServices project exists
 * yet (the installer hasn't reached its foundry-create phase) — we tolerate that
 * window and retry until one appears or we time out.
 *
 * Only retries on FOUNDRY_PROJECT_ZERO (no project yet). Any other error (auth
 * failure, config issue, etc.) is re-thrown immediately so it isn't masked as a
 * misleading "Foundry is not ready" timeout.
 */
export async function resolveFoundryEndpointWithWait(
  args: { credential: TokenCredential; subscriptionId: string; endpoint?: string },
  opts: { pollMs?: number; timeoutMs?: number; onWait?: (msg: string) => void } = {},
): Promise<FoundryEndpointInfo> {
  const pollMs = opts.pollMs ?? 10_000;
  const timeoutMs = opts.timeoutMs ?? 15 * 60_000;
  const deadline = Date.now() + timeoutMs;
  const sleep = (ms: number) => new Promise<void>((r) => setTimeout(r, ms));

  for (;;) {
    try {
      const p = await resolveFoundryProject({
        credential: args.credential,
        subscriptionId: args.subscriptionId,
        interactive: false,
        // When the subscription holds multiple projects (e.g. a founder reinstalling
        // alongside an existing platform), an explicit endpoint disambiguates instead
        // of FOUNDRY_PROJECT_MULTIPLE. Omitted → single-project auto-discovery.
        endpoint: args.endpoint,
      });
      return { endpoint: p.endpoint, accountScope: p.accountScope };
    } catch (e) {
      if (e instanceof LocalCliError && e.code === "FOUNDRY_PROJECT_MULTIPLE") {
        throw new LocalCliError({
          code: "BOOTSTRAP_UI_MULTIPLE_PROJECTS",
          message: `${e.message} bootstrap ui can't pick one safely (it must not deploy Simple Stacey into the wrong project).`,
          hint: "Re-run with the throwaway project's endpoint, e.g. 'm8t bootstrap ui --repo-root \"$(pwd)\" --endpoint https://<account>.services.ai.azure.com/api/projects/m8t'. The installer prints it in 'm8t bootstrap status'.",
          cause: e,
        });
      }
      if (!(e instanceof LocalCliError && e.code === "FOUNDRY_PROJECT_ZERO")) {
        throw e;
      }
      if (Date.now() >= deadline) {
        throw new LocalCliError({
          code: "BOOTSTRAP_UI_FOUNDRY_NOT_READY",
          message: "Foundry is not ready yet — no AI project appeared in your subscription before the timeout.",
          hint: "Check 'm8t bootstrap status --watch'; the install may have stalled in foundry-create.",
          cause: e,
        });
      }
      opts.onWait?.("waiting for Foundry-create to finish…");
      await sleep(pollMs);
    }
  }
}

/** OID of the signed-in user (for the role assignment). */
export async function getSignedInUserOid(): Promise<string> {
  const oid = (await runAz(["ad", "signed-in-user", "show", "--query", "id", "-o", "tsv"])).trim();
  if (!oid) {
    throw new LocalCliError({
      code: "BOOTSTRAP_UI_NO_SIGNED_IN_USER",
      message: "Could not resolve the signed-in user (az ad signed-in-user show returned nothing).",
      hint: "Personal Microsoft accounts can't be resolved this way — sign in with a work/school account.",
    });
  }
  return oid;
}

/**
 * Grants the founder the Foundry data-plane role on the account (idempotent).
 * Delegates to rbac.ts grantFoundryUser (REST PUT, okStatuses: [409]) so re-runs
 * are safe — az role assignment create is NOT reliably idempotent (409 exits non-zero).
 *
 * If the live smoke shows prompt-agent createVersion/invoke still 403s after RBAC
 * propagation, escalate by additionally granting a broader role (e.g. Cognitive Services
 * Contributor / Azure AI Developer) — see DESIGN §8.
 */
export async function ensureFounderFoundryRole(args: {
  credential: TokenCredential;
  subscriptionId: string;
  principalId: string;
  accountScope: string;
}): Promise<void> {
  await grantFoundryUser({
    credential: args.credential,
    subscriptionId: args.subscriptionId,
    accountScope: args.accountScope,
    principalId: args.principalId,
    principalType: "User",
  });
}

/**
 * Write apps/web/.env.local for the onboarding chat-only run. STORAGE_ACCOUNT_NAME
 * is left EMPTY so the gateway boot is skipped (chat-only); the webapp forwards the
 * browser MSAL token to Foundry — no platform deps. Backs up any existing file to
 * .env.local.bak so a developer's local config isn't silently clobbered.
 */
export function writeWebEnvLocal(args: {
  repoRoot: string; tenantId: string; clientId: string; foundryEndpoint: string;
}): string {
  const envPath = path.join(args.repoRoot, "apps", "web", ".env.local");
  if (fs.existsSync(envPath)) {
    fs.copyFileSync(envPath, envPath + ".bak");
  }
  const body = [
    "# Written by `m8t bootstrap ui` — onboarding chat-only run (Simple Stacey).",
    "# STORAGE unset → gateway boot skipped; the webapp forwards your MSAL token to Foundry.",
    `AZURE_TENANT_ID=${args.tenantId}`,
    `AZURE_CLIENT_ID=${args.clientId}`,
    `FOUNDRY_PROJECT_ENDPOINT=${args.foundryEndpoint}`,
    "STORAGE_ACCOUNT_NAME=",
    "M8T_DISABLE_WORKER=1",
    "M8T_ONBOARDING=1",
    "NEXT_PUBLIC_M8T_ONBOARDING=1",
    "",
  ].join("\n");
  fs.writeFileSync(envPath, body, "utf8");
  return envPath;
}

/** Throw a clear error if the Node major is < 20 (apps/web needs Node 20+). */
export function assertNodeVersion(versionString: string = process.version): void {
  const major = Number(/^v?(\d+)\./.exec(versionString)?.[1] ?? "0");
  if (major < 20) {
    throw new LocalCliError({
      code: "BOOTSTRAP_UI_NODE_TOO_OLD",
      message: `apps/web needs Node 20+, but this is ${versionString}.`,
      hint: "Install Node 20+ (see install/prereqs-*.md), then re-run 'm8t bootstrap ui'.",
    });
  }
}

/** Run a command in `cwd`, inheriting stdio, rejecting with a LocalCliError on non-zero. */
export function runInherit(cmd: string, cmdArgs: string[], cwd: string, failCode: string): Promise<void> {
  return new Promise((resolve, reject) => {
    const child = spawn(cmd, cmdArgs, { cwd, stdio: "inherit", shell: process.platform === "win32" });
    child.on("error", (e) => { reject(new LocalCliError({ code: failCode, message: `Failed to start '${cmd}': ${e.message}` })); });
    child.on("close", (code) => {
      if (code === 0) {
        resolve();
      } else {
        const opts: { code: string; message: string; hint?: string } = {
          code: failCode,
          message: `'${cmd} ${cmdArgs.join(" ")}' exited with code ${String(code)}.`,
        };
        if (cmd === "pnpm") {
          opts.hint = "Fix the pnpm error above, then re-run 'm8t bootstrap ui' (it's idempotent).";
        }
        reject(new LocalCliError(opts));
      }
    });
  });
}

/** corepack enable + pnpm install at the repo root (installs the whole workspace). */
export async function installWebDeps(repoRoot: string): Promise<void> {
  assertNodeVersion();
  await runInherit("corepack", ["enable"], repoRoot, "BOOTSTRAP_UI_COREPACK_FAILED").catch(() => {
    // corepack enable can need elevated perms on some setups; pnpm may already be on PATH.
    // Non-fatal — let pnpm install surface a real "pnpm not found" if it truly isn't there.
  });
  await runInherit("pnpm", ["install"], repoRoot, "BOOTSTRAP_UI_INSTALL_FAILED");
}

/** Paths used by the detached onboarding-UI process helpers. */
export function onboardingUiPaths(home: string = os.homedir()): { logPath: string; pidPath: string } {
  const dir = path.join(home, ".m8t-stack");
  return {
    logPath: path.join(dir, "onboarding-ui.log"),
    pidPath: path.join(dir, "onboarding-ui.pid"),
  };
}

/**
 * Start `pnpm --filter web dev` detached so it survives the CLI exit.
 *
 * - If the port is already serving, returns immediately with `alreadyRunning: true`
 *   so we never spawn a second dev server.
 * - Otherwise spawns with `detached: true` + `child.unref()` — the child becomes a
 *   process-group leader; `stopOnboardingUi` kills the whole group via `process.kill(-pid)`.
 * - Polls readiness up to ~45 s (500 ms intervals). If the server hasn't answered by
 *   then, returns anyway — it's still compiling; the caller prints a "check logs" note.
 */
export async function serveOnboardingUiDetached(args: {
  repoRoot: string;
  port: string;
}): Promise<{ alreadyRunning: boolean; logPath: string }> {
  const { logPath, pidPath } = onboardingUiPaths();
  const portNum = Number(args.port);

  // Check if something is already serving on the port.
  const isPortOpen = (): Promise<boolean> =>
    new Promise((resolve) => {
      const s = net.createConnection({ host: "127.0.0.1", port: portNum });
      s.setTimeout(1000);
      s.on("connect", () => { s.destroy(); resolve(true); });
      s.on("error", () => { resolve(false); });
      s.on("timeout", () => { s.destroy(); resolve(false); });
    });

  if (await isPortOpen()) {
    return { alreadyRunning: true, logPath };
  }

  // Open log file (append) and spawn detached.
  fs.mkdirSync(path.dirname(logPath), { recursive: true });
  const fd = fs.openSync(logPath, "a");
  const child = spawn("pnpm", ["--filter", "web", "dev"], {
    cwd: args.repoRoot,
    detached: true,
    stdio: ["ignore", fd, fd],
    env: { ...process.env, PORT: args.port },
    shell: false,
  });
  fs.closeSync(fd);
  // A synchronous spawn failure (e.g. pnpm not on PATH) leaves pid undefined —
  // fail loudly here instead of writing "undefined" to the pid file and then
  // polling for 45 s on a server that will never come up.
  if (child.pid == null) {
    throw new LocalCliError({
      code: "BOOTSTRAP_UI_DEV_SPAWN_FAILED",
      message: "Could not start the web dev server (pnpm did not spawn).",
      hint: "Check that pnpm + Node 20+ are installed; see the log at " + logPath + ".",
    });
  }
  child.unref();

  // Persist the pid so --stop can kill the process group later.
  fs.writeFileSync(pidPath, String(child.pid), "utf8");

  // Poll readiness (up to ~45 s).
  const deadline = Date.now() + 45_000;
  const sleep = (ms: number) => new Promise<void>((r) => setTimeout(r, ms));
  while (Date.now() < deadline) {
    await sleep(500);
    if (await isPortOpen()) break;
  }

  return { alreadyRunning: false, logPath };
}

/** Best-effort browser open for the onboarding chat. Fire-and-forget: never
 *  throws, so a headless box still gets the printed URL fallback. */
export function autoOpenOnboardingUi(
  url: string,
  open: (u: string) => void | Promise<void> = tryOpenUrl,
): void {
  try {
    const result = open(url);
    if (result instanceof Promise) {
      result.catch(() => { /* best-effort */ });
    }
  } catch { /* best-effort */ }
}

/**
 * Stop the detached onboarding UI by killing its process group.
 *
 * Returns true if a pid file was found (regardless of whether the process was
 * still alive), false if there was nothing to stop.
 */
export function stopOnboardingUi(home: string = os.homedir()): boolean {
  const { pidPath } = onboardingUiPaths(home);
  let pidStr: string;
  try {
    pidStr = fs.readFileSync(pidPath, "utf8").trim();
  } catch {
    return false; // no pid file — nothing to stop
  }

  const pid = Number(pidStr);
  if (!Number.isNaN(pid) && pid > 0) {
    try {
      // Negative pid = kill the entire process group (child was spawned detached).
      process.kill(-pid, "SIGTERM");
    } catch (e) {
      // ESRCH = process already gone — treat as success.
      if ((e as NodeJS.ErrnoException).code !== "ESRCH") throw e;
    }
  }

  try { fs.unlinkSync(pidPath); } catch { /* already gone */ }
  return true;
}
